import { Box, Container, Flex, Heading, Text } from "@radix-ui/themes";
import { Helmet } from "react-helmet-async";
import { TitleBlock } from "../TitleBlock";
import { SITE_HOST, SITE_URL, siteUrl } from "../../lib/site";

export function TermsPage() {
  return (
    <Container size="4" className="route-page">
      <Helmet>
        <title>Terms &amp; Conditions | Iris</title>
        <meta
          name="description"
          content="Terms of use for the IrisChat AI companion service."
        />
        <link rel="canonical" href={siteUrl("/terms-conditions/")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl("/terms-conditions/")} />
        <meta property="og:title" content="Terms &amp; Conditions | Iris" />
        <meta
          property="og:description"
          content="Terms of use for the IrisChat AI companion service."
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Terms &amp; Conditions | Iris" />
        <meta
          name="twitter:description"
          content="Terms of use for the IrisChat AI companion service."
        />
      </Helmet>
      <Flex direction="column" gap="6">
        <TitleBlock title="Terms & Conditions" />

        {/* Intro */}
        <Flex direction="column" gap="4">
          <Text size="3" className="route-body">
            These Terms of Use ("Terms of Use") are entered into by and between
            you and Flagship Labs 105, Inc. ("FL105", "we", "us," or "our") and
            govern your access to and use of IrisChat, including the{" "}
            <a href={SITE_URL} className="route-link">
              {SITE_HOST}
            </a>{" "}
            website and the Iris AI companion service (together, the "Service").
            The term "you," as used in these Terms of Use, means any person who
            accesses or uses the Service.
          </Text>
          <Text size="3" className="route-body">
            Please read these Terms of Use carefully before you use the Service.
            By using the Service, you accept and agree to be bound and abide by
            these Terms of Use. If you do not accept these Terms of Use, please
            do not access or use the Service.
          </Text>
        </Flex>

        {/* 1 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">1. Changes To The Terms Of Use</Heading>
            <Text size="3" className="route-body">
              We may revise and update these Terms of Use from time to time at
              our sole discretion. All changes are effective immediately when we
              post them, and your continued use of the Service following the
              posting of revised Terms of Use means that you accept and agree to
              the changes.
            </Text>
          </Flex>
        </Box>

        {/* 2 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">2. Access</Heading>
            <Text size="3" className="route-body">
              Our Service is designed to work across multiple messaging
              platforms. Currently supported platforms include Apple iMessage
              and RCS, and we may add or remove supported platforms over time.
              This means:
            </Text>
            <ul className="route-list">
              <li>
                <Text size="3" className="route-body">
                  You must have a device and messaging app capable of sending
                  and receiving messages via a supported messaging platform.
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  The device must be associated with a valid, active phone
                  number or account on that platform.
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  Our Service may not function on all devices or messaging
                  clients. For a current list of supported platforms, or if you
                  are unsure whether your device is supported, please contact us
                  at{" "}
                  <a href="mailto:privacy@irischat.ai" className="route-link">
                    privacy@irischat.ai
                  </a>
                  .
                </Text>
              </li>
            </ul>
            <Text size="3" className="route-body">
              We reserve the right to terminate, alter, or amend the Service, or
              any service or material we provide in connection with the Service,
              in our sole discretion without notice. We will not be liable if
              for any reason all or any part of the Service is unavailable at
              any time or for any period.
            </Text>
          </Flex>
        </Box>

        {/* 3 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">3. Age Requirement</Heading>
            <Text size="3" className="route-body">
              The Service is intended for individuals 18 years of age or older.
              By accessing or using the Service, you represent and warrant that
              you are at least 18 years old. If you are under 18, you are not
              permitted to use the Service. We do not knowingly permit
              individuals under 18 to use the Service. If we learn that a user
              is under 18, we will terminate their access and delete associated
              data.
            </Text>
          </Flex>
        </Box>

        {/* 4 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">4. Artificial Intelligence Disclosure</Heading>
            <Text size="3" className="route-body">
              Iris is powered by artificial intelligence. When you interact with
              Iris, you are communicating with an AI system, not a human being.
              Iris's responses are generated automatically and may not always be
              accurate, appropriate, or complete.
            </Text>
          </Flex>
        </Box>

        {/* 5 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">5. Health and Wellness Disclaimer</Heading>
            <Text size="3" className="route-body">
              IrisChat is a wellness and emotional support service. It is not a
              medical or clinical service. Iris is not intended to diagnose,
              treat, cure, or prevent any disease or mental health disorder, and
              is not a substitute for professional medical, psychiatric,
              psychological, or therapeutic care.
            </Text>
            <Text size="3" className="route-body">
              Do not use Iris in place of seeking professional help. If you are
              experiencing a mental health emergency, thoughts of self-harm or
              suicide, or a medical emergency, contact emergency services (911),
              the 988 Suicide &amp; Crisis Lifeline (call or text 988), or the
              Crisis Text Line (text HOME to 741741) immediately. Do not use
              Iris to report an emergency.
            </Text>
            <Text size="3" className="route-body">
              Use of the Service does not create a physician-patient,
              therapist-client, counselor-client, attorney-client, or any other
              privileged or confidential relationship between you and FL105 or
              any of its personnel.
            </Text>
            <Text size="3" className="route-body">
              Safety monitoring within the Service is performed by automated
              systems, not by licensed clinicians or mental health
              professionals. Iris does not include features to alert or notify
              healthcare providers, emergency services, or any third parties on
              your behalf, except as required by law.
            </Text>
          </Flex>
        </Box>

        {/* 6 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">6. Safety Resources and Limitations</Heading>
            <Text size="3" className="route-body">
              Iris includes automated safety features designed to detect certain
              types of crisis-related content and, where detected, display
              mental health and crisis resources. These automated systems are
              not guaranteed to identify all situations that may require
              intervention.
            </Text>
            <Text size="3" className="route-body">
              You should not rely on Iris to recognize, detect, or respond to a
              mental health crisis or medical emergency. If you or someone you
              know is experiencing a mental health crisis, suicidal thoughts, or
              a medical emergency, please contact emergency services (911) or
              the 988 Suicide &amp; Crisis Lifeline (call or text 988)
              immediately. Do not use Iris to report an emergency.
            </Text>
            <Text size="3" className="route-body">
              For more information about IrisChat's safety protocols, please
              visit{" "}
              <a href="/safety-protocol/" className="route-link">
                {SITE_HOST}/safety-protocol/
              </a>
              .
            </Text>
          </Flex>
        </Box>

        {/* 7 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">7. Intellectual Property Rights</Heading>
            <Text size="3" className="route-body">
              The Service and its entire contents, features, and functionality
              (including but not limited to all information, software, text,
              displays, images, video and audio, and the design, selection, and
              arrangement thereof), are owned by FL105, its licensors or other
              providers of such material and are protected by United States and
              international copyright, trademark, patent, trade secret, and
              other intellectual property rights laws. All rights not
              specifically granted herein are reserved by FL105.
            </Text>
            <Text size="3" className="route-body">
              These Terms of Use permit you to use the Service for your personal
              use only. You must not reproduce, distribute, modify, create
              derivative works of, publicly display, publicly perform,
              republish, download, store, or transmit any of the material on our
              Service except for your personal use.
            </Text>
            <Text size="3" className="route-body">
              No right, title, or interest in or to the Service or any content
              on the Service is transferred to you under any circumstances, and
              all rights not expressly granted are reserved by FL105. Any use of
              the Service not expressly permitted by these Terms of Use is a
              breach of these Terms of Use and may violate copyright, trademark,
              and other laws.
            </Text>
          </Flex>
        </Box>

        {/* 8 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">8. Trademarks</Heading>
            <Text size="3" className="route-body">
              All trademarks, service marks, logos, and trade names which appear
              on the Service, whether registered or not (including but not
              limited to: the word marks "Iris", "IrisChat", and logos thereof)
              (the "Marks") are and remain the exclusive property of FL105 or
              its licensors (as applicable) and are protected by applicable
              intellectual property and trademark laws and treaties. You may not
              use, copy, reproduce, republish, upload, post, transmit,
              distribute, or modify any of the Marks in any way, including in
              advertising or publicity pertaining to distribution of materials
              on the Service, without FL105's prior consent, which shall be in
              FL105's sole discretion. The use of any of the Marks on any other
              website is prohibited without the express written consent of
              FL105.
            </Text>
          </Flex>
        </Box>

        {/* 9 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">9. Prohibited Uses</Heading>
            <Text size="3" className="route-body">
              You may use the Service only for lawful purposes and in accordance
              with these Terms of Use. You agree not to use the Service in any
              way that violates any applicable federal, state, local or
              international law or regulation (including, without limitation,
              any laws regarding the export of data or software to and from the
              US or other countries).
            </Text>
          </Flex>
        </Box>

        {/* 10 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">10. No Reliance On Information</Heading>
            <Text size="3" className="route-body">
              To the extent permitted by applicable law: The information
              presented on or through the Service is made available solely for
              general informational purposes. We do not warrant the accuracy,
              completeness, timeliness, or usefulness of this information. Any
              reliance you place on such information is strictly at your own
              risk. We disclaim all liability and responsibility arising from
              any reliance placed on such materials by you or any other visitor
              to the Service, or by anyone who may be informed of any of its
              contents.
            </Text>
            <Text size="3" className="route-body">
              While we may periodically update or correct information presented
              through the Service, such information may include typographical
              errors and/or technical inaccuracies and/or omissions. We make no
              representation or warranty as to the accuracy of any information
              on the Service and expressly disclaim any obligation to update
              such information. We also reserve the right to make additions,
              deletions, or modifications to any information at any time and
              from time to time without any prior notice.
            </Text>
            <Text size="3" className="route-body">
              The Website may include content and/or materials provided by third
              parties. All statements and/or opinions expressed in such content
              and materials are solely the opinions and the responsibility of
              the person or entity providing such content or materials and do
              not necessarily reflect the opinion of FL105. We are not
              responsible, or liable to you or any third party, for the content
              or accuracy of any content or materials provided by any third
              parties.
            </Text>
          </Flex>
        </Box>

        {/* 11 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">11. Privacy</Heading>
            <Text size="3" className="route-body">
              Our{" "}
              <a href="/privacy/" className="route-link">
                Privacy Policy
              </a>{" "}
              describes our collection, use, and disclosure of personal
              information in connection with the Service. Our Privacy Policy is
              expressly incorporated into these Terms of Use, and by using the
              Service you agree to the collection, use, and disclosure practices
              in our Privacy Policy.
            </Text>
          </Flex>
        </Box>

        {/* 12 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">12. Disclaimer Of Warranties</Heading>
            <Text size="3" className="route-body">
              YOUR USE OF THE SERVICE, ITS CONTENT, AND ANY SERVICES OBTAINED
              THROUGH THE SERVICE IS AT YOUR OWN RISK. THE SERVICE, ITS CONTENT
              AND ANY SERVICES OBTAINED THROUGH OR IN CONNECTION WITH THE
              SERVICE ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS,
              WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE
              DISCLAIM ALL OTHER WARRANTIES, EXPRESS OR IMPLIED, INCLUDING,
              WITHOUT LIMITATION, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
              FOR A PARTICULAR PURPOSE, TITLE AND NON-INFRINGEMENT. NEITHER
              FL105 NOR ANY PERSON ASSOCIATED WITH FL105 MAKES ANY WARRANTY OR
              REPRESENTATION WITH RESPECT TO THE COMPLETENESS, SECURITY,
              RELIABILITY, QUALITY, ACCURACY OR AVAILABILITY OF THE SERVICE.
              WITHOUT LIMITING THE FOREGOING, NEITHER FL105 NOR ANYONE
              ASSOCIATED WITH FL105 REPRESENTS OR WARRANTS THAT THE SERVICE, ITS
              CONTENT OR ANY SERVICES OBTAINED THROUGH OR IN CONNECTION WITH THE
              SERVICE WILL BE ACCURATE, RELIABLE, ERROR-FREE OR UNINTERRUPTED,
              THAT DEFECTS WILL BE CORRECTED, THAT OUR SERVICE OR THE SERVER
              THAT MAKES IT AVAILABLE IS FREE OF VIRUSES OR OTHER HARMFUL
              COMPONENTS OR THAT THE SERVICE OR ANY SERVICES OBTAINED THROUGH OR
              IN CONNECTION WITH THE SERVICE WILL OTHERWISE MEET YOUR NEEDS OR
              EXPECTATIONS. WE ARE NOT RESPONSIBLE FOR TYPOGRAPHICAL ERRORS OR
              OMISSIONS. WHILE WE ATTEMPT TO MAKE YOUR ACCESS AND USE OF THE
              SERVICE SAFE, WE CANNOT AND DO NOT REPRESENT OR WARRANT THAT OUR
              SERVICE OR ITS SERVER(S) ARE FREE OF VIRUSES OR OTHER HARMFUL
              COMPONENTS. THE FOREGOING DOES NOT AFFECT ANY WARRANTIES WHICH
              CANNOT BE EXCLUDED OR LIMITED UNDER APPLICABLE LAW.
            </Text>
            <Text size="3" className="route-body">
              IN THE EVENT OF ANY PROBLEM WITH THE SERVICE OR ITS CONTENT, YOU
              AGREE THAT YOUR SOLE REMEDY IS TO CEASE ACCESSING AND USING THE
              SERVICE AND THE CONTENT.
            </Text>
          </Flex>
        </Box>

        {/* 13 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">13. Subscription Term &amp; Auto-Renewal</Heading>
            <Text size="3" className="route-body">
              All subscriptions are billed on a recurring basis at the agreed
              upon frequency at the time of purchase and automatically renew at
              the end of each billing cycle unless canceled in accordance with
              this policy. By subscribing, you authorize us to charge your
              payment method on a recurring basis.
            </Text>
          </Flex>
        </Box>

        {/* 14 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">14. Cancellation Policy</Heading>
            <Text size="3" className="route-body">
              You may cancel your subscription at any time through messaging
              Iris to cancel your subscription or by contacting our support team
              at{" "}
              <a href="mailto:support@irischat.ai" className="route-link">
                support@irischat.ai
              </a>
              .
            </Text>
            <Text size="3" className="route-body">
              Cancellations take effect at the end of your current billing
              period.
            </Text>
            <ul className="route-list">
              <li>
                <Text size="3" className="route-body">
                  You will retain full access to Iris until the end of that
                  billing cycle.
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  We do not provide prorated refunds for partial months or
                  unused time.
                </Text>
              </li>
            </ul>
            <Text size="3" className="route-body">
              To avoid being charged for the next billing cycle, cancellations
              must be completed prior to the renewal date.
            </Text>
          </Flex>
        </Box>

        {/* 15 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">15. Refund Policy</Heading>
            <Text size="3" className="route-body">
              As a general policy, all subscription fees are non-refundable.
              However, exceptions may be made in the following circumstances:
            </Text>
            <ul className="route-list">
              <li>
                <Text size="3" className="route-body">
                  Duplicate charges or billing errors
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  Other cases required by applicable law
                </Text>
              </li>
            </ul>
            <Text size="3" className="route-body">
              All refund requests must be submitted within 7 days of the charge
              date and will be reviewed on a case-by-case basis. Approved
              refunds will be issued to the original method of payment. Please
              allow 10–15 business days for the refunded amount to be reflected
              on your account statement.
            </Text>
          </Flex>
        </Box>

        {/* 16 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">16. Failure To Cancel Before Renewal</Heading>
            <Text size="3" className="route-body">
              If you do not cancel your subscription before the renewal date,
              the subscription will automatically renew, and the applicable fee
              will be charged. We are not obligated to provide refunds for
              charges incurred due to failure to cancel prior to renewal.
            </Text>
          </Flex>
        </Box>

        {/* 17 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">17. Account Termination By Us</Heading>
            <Text size="3" className="route-body">
              We reserve the right to suspend or terminate your account in
              accordance with our Terms of Service. In such cases, refunds will
              not be provided unless required by law.
            </Text>
          </Flex>
        </Box>

        {/* 18 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">18. Limitation On Liability</Heading>
            <Text size="3" className="route-body">
              IN NO EVENT WILL FL105, ITS AFFILIATES OR THEIR LICENSORS, SERVICE
              PROVIDERS, EMPLOYEES, AGENTS, OFFICERS OR DIRECTORS BE LIABLE FOR
              DAMAGES OF ANY KIND, UNDER ANY LEGAL THEORY, ARISING OUT OF OR IN
              CONNECTION WITH YOUR USE, OR INABILITY TO USE, THE SERVICE, ANY
              WEBSITES LINKED TO IT, ANY CONTENT ON THE SERVICE OR SUCH OTHER
              WEBSITES OR ANY SERVICES OBTAINED THROUGH OR IN CONNECTION WITH
              THE SERVICE OR SUCH OTHER WEBSITES, INCLUDING ANY DIRECT,
              INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL OR PUNITIVE DAMAGES,
              INCLUDING BUT NOT LIMITED TO, PERSONAL INJURY, PAIN AND SUFFERING,
              EMOTIONAL DISTRESS, LOSS OF REVENUE, LOSS OF PROFITS, LOSS OF
              BUSINESS OR ANTICIPATED SAVINGS, LOSS OF USE, LOSS OF GOODWILL,
              LOSS OF DATA, AND WHETHER CAUSED BY TORT (INCLUDING NEGLIGENCE),
              BREACH OF CONTRACT OR OTHERWISE, EVEN IF FORESEEABLE. SOME STATES
              DO NOT ALLOW THE EXCLUSION OR LIMITATION OF INCIDENTAL OR
              CONSEQUENTIAL DAMAGES, SO THE ABOVE LIMITATION OR EXCLUSION MAY
              NOT APPLY TO YOU.
            </Text>
          </Flex>
        </Box>

        {/* 19 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">19. Indemnification</Heading>
            <Text size="3" className="route-body">
              You agree to indemnify, defend, and hold FL105 (and its affiliated
              companies, contractors, employees, directors, officers, licensors,
              and agents) harmless from any and all claims, suits, actions,
              losses, costs, damages, and any other liabilities, including
              attorneys' fees, brought by a third party arising out of or
              related to (i) your use or misuse of the Service; (ii) any
              violation of applicable law or the rights of any other person or
              entity by you; or (iii) any breach or violation by you of these
              Terms of Use.
            </Text>
          </Flex>
        </Box>

        {/* 20 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">20. International Users</Heading>
            <Text size="3" className="route-body">
              We operate the Service from the United States of America. If you
              choose to access the Service from outside of the United States,
              you are responsible for complying with applicable local laws.
              Please refer to our Privacy Policy for information regarding
              collection of personal information.
            </Text>
          </Flex>
        </Box>

        {/* 21 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">21. Severability; Waiver</Heading>
            <Text size="3" className="route-body">
              If any provision of these Terms of Use are held unlawful, void, or
              for any reason unenforceable, then that provision is deemed
              severable from these Terms of Use and will not affect the validity
              and enforceability of any remaining provisions. A provision of
              these Terms of Use may be waived only by a written instrument
              executed by the party entitled to the benefit of such provision.
              The failure of any party at any time to require performance of any
              provision of these Terms of Use will in no manner affect such
              party's right at a later time to enforce the same. A waiver of any
              breach of any provision of these Terms of Use is not construed as
              a continuing waiver of other breaches of the same or other
              provisions of these Terms of Use.
            </Text>
          </Flex>
        </Box>

        {/* 22 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">22. Governing Law</Heading>
            <Text size="3" className="route-body">
              These Terms of Use are governed by the laws of the State of
              Delaware without giving effect to any principles of conflict of
              law.
            </Text>
          </Flex>
        </Box>

        {/* 23 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">23. Consent to Receive Communications</Heading>
            <Text size="3" className="route-body">
              By interacting with Iris through any supported messaging platform,
              you agree to receive messages from IrisChat (operated by FL105,
              Inc.) at the phone number or account you used to contact us.
              Message and data rates may apply. You may opt out at any time by
              replying STOP.
            </Text>
          </Flex>
        </Box>

        {/* 24 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">24. Email Communications</Heading>
            <Text size="3" className="route-body">
              During your interaction, you may choose to provide your email
              address. By doing so, you consent to receive emails from FL105 for
              the purposes of providing information, updates, offers, and other
              communications related to our products or services. You can
              withdraw your consent at any time by clicking the "unsubscribe"
              link in any email we send or by contacting us at{" "}
              <a href="mailto:support@irischat.ai" className="route-link">
                support@irischat.ai
              </a>
              .
            </Text>
          </Flex>
        </Box>

        {/* 25 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">25. Use of Information</Heading>
            <Text size="3" className="route-body">
              We collect and use your contact information (phone number, email
              address, and any other data you provide) in accordance with our{" "}
              <a href="/privacy/" className="route-link">
                Privacy Policy
              </a>
              . We will not sell or share your personal information with third
              parties without your consent, except as required by law.
            </Text>
          </Flex>
        </Box>

        {/* 26 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">26. Opt-Out</Heading>
            <Text size="3" className="route-body">
              You may opt out of messaging communications at any time by:
            </Text>
            <ul className="route-list">
              <li>
                <Text size="3" className="route-body">
                  Stopping your conversation with Iris.
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  Replying STOP to any message.
                </Text>
              </li>
            </ul>
            <Text size="3" className="route-body">
              You may opt out of email communications by following the
              unsubscribe instructions included in our emails.
            </Text>
          </Flex>
        </Box>

        {/* 27 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">27. Feedback</Heading>
            <Text size="3" className="route-body">
              If you submit or otherwise make available to us any feedback,
              suggestions, ideas, comments, improvements, or other input
              relating to our products, services, or business ("Feedback"), you
              hereby grant us a non-exclusive, worldwide, perpetual,
              irrevocable, fully paid-up, royalty-free, sublicensable, and
              transferable license to use, reproduce, modify, create derivative
              works from, distribute, publicly display, and otherwise exploit
              such Feedback for any purpose without compensation, attribution,
              or obligation of any kind to you. You acknowledge that we are not
              obligated to use any Feedback and that we may already be
              developing or have developed ideas similar to your Feedback.
              Nothing in this provision limits any rights we may have to
              independently develop, acquire, or market products or services
              that may be similar to or competitive with any Feedback.
            </Text>
          </Flex>
        </Box>

        {/* 28 */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">28. Contact Information</Heading>
            <Text size="3" className="route-body">
              If you have questions about these terms, billing questions,
              cancellation assistance, refund requests, or how we communicate
              with you, contact us at:
            </Text>
            <Text size="3" className="route-body">
              Email:{" "}
              <a href="mailto:support@irischat.ai" className="route-link">
                support@irischat.ai
              </a>
            </Text>
          </Flex>
        </Box>
      </Flex>
    </Container>
  );
}
